import { selectCurrentHotelId, selectCurrentHotelName } from 'accessors'
import {
  PrimaryRegularButton,
  SecondaryRegularButton
} from 'components/ReusableComponents/Button/RegularButtons'
import Dialog from 'components/ReusableComponents/Dialog'
import SmsIcon from 'components/ReusableComponents/Icons/Sms'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import AnnouncementConfirmation from 'features/Announcements/AnnouncementConfirmation'
import MainTemplate from 'features/CustomCampaigns/EditCampaign/MainTemplate'
import { EditPromo } from 'features/ManageCampaigns/EditPromoDialog/EditPromo'
import { TextPreview, TextPreviewBubble } from 'features/ManageCampaigns/TextPreview'
import { getImgUrlFromCDN } from 'features/ManageCampaigns/utils/campaignHelpers'
import { mediaTags } from 'features/ManageCampaigns/utils/constants'
import useFirstRender from 'hooks/useFirstRender'
import usePlausible from 'hooks/usePlausible'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useToasts } from 'react-toast-notifications'
import announcementApi from 'services/api/announcement'
import { getApiErrorMessage } from 'services/helpers/campaignApiHelpers'

import { inPropertyAudiences, inPropertyAudienceTypes } from '../constants'
import styles from './EditTextDialog.module.scss'

const steps = {
  mainTemplate: 'mainTemplate',
  mainPromo: 'mainPromo'
}

const stepNames = {
  mainTemplate: 'Text content',
  mainPromo: 'Promotion (optional)'
}

function getSteps(audience) {
  if (audience === inPropertyAudienceTypes.DayBeforeCheckOut) {
    return steps
  }

  return { mainTemplate: steps.mainTemplate }
}

const mediaTag = mediaTags.announcement

const EditTextDialog = ({
  onClose,
  announcementType,
  audience,
  defaultText = '',
  totalEligibleForText,
  committedRemaining,
  onScheduledCountUpdated,
  announcementIdBeingEdited,
  refreshResults
}) => {
  const { postEvent, plausibleEvents } = usePlausible()

  function handleTextChange(text) {
    setMessageTemplate(text.replace(/inviteLink__\)(\S)/, 'inviteLink__) $1'))
  }

  const [isEditing, setIsEditing] = useState(false)
  const [isEditingRecurring, setIsEditingRecurring] = useState(false)
  const [editingAnnouncementType, setEditingAnnouncementType] = useState()
  const [editingAnnouncementRepeatKind, setEditingAnnouncementRepeatKind] = useState()
  const [editingAnnouncementDaysOfWeek, setEditingAnnouncementDaysOfWeek] = useState()
  const [editingStartDate, setEditingStartDate] = useState()
  const [editingEndDate, setEditingEndDate] = useState()

  const [activeStep, setActiveStep] = useState(steps.mainTemplate)
  const [nextStep, setNextStep] = useState()

  const [usedAllTags, setUsedAllTags] = useState(true)
  const [messageMedia, setMessageMedia] = useState()
  const [messageTemplate, setMessageTemplate] = useState(defaultText)

  const [promotionCode, setPromotionCode] = useState()
  const [maxDiscountPercentage, setMaxDiscountPercentage] = useState()
  const [minNightsToStay, setMinNightsToStay] = useState()
  const [promoTagline, setPromoTagline] = useState('Sample promo taglne')
  const [hasPromoValidityDate, setHasPromoValidityDate] = useState(false)
  const [promoValidityStartDate, setPromoValidityStartDate] = useState()
  const [promoValidityEndDate, setPromoValidityEndDate] = useState()
  const [isUsingDiscount, setIsUsingDiscount] = useState(!!promotionCode)

  const isPromoValid = isUsingDiscount
    ? !!promotionCode?.match(/^[A-Za-z0-9]{1,15}$/gm)
    : true

  const userName = useSelector(s => s.login.currentUser.name)
  const isHotelSetupCompleted = useSelector(s => s.hotelDetails.isPreOnboardingCompleted)
  const hotelName = useSelector(selectCurrentHotelName)
  const hotelId = useSelector(selectCurrentHotelId)

  const [isSendingTestMessage, setIsSendingTestMessage] = useState(false)

  const { addToast } = useToasts()

  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (announcementIdBeingEdited) {
      announcementApi
        .getRecurringAnnouncement(announcementIdBeingEdited)
        .then(data => {
          setMessageTemplate(data.messageTemplate)
          setPromotionCode(data.promotionCode)
          setMinNightsToStay(data.minNightsToStay)
          setMaxDiscountPercentage(data.maxDiscountPercentage)
          setPromoTagline(data.promotionTagline)
          setHasPromoValidityDate(data.hasPromoValidityDate)
          setPromoValidityStartDate(
            data.promoValidityStartDate
              ? data.promoValidityStartDate.split('T')[0]
              : undefined
          )
          setMessageMedia(data.messageMediaUrls)
          setPromoValidityEndDate(
            data.promoValidityEndDate
              ? data.promoValidityEndDate.split('T')[0]
              : undefined
          )
          setIsUsingDiscount(!!data.promotionCode)
          setEditingAnnouncementDaysOfWeek(data.daysOfWeek || [])
          setEditingAnnouncementRepeatKind(data.repeatKind)
          setEditingStartDate(data.deliveryStartDate)
          setEditingEndDate(data.deliveryFinishDate)
        })
        .catch(() => {
          addToast(`Couldn't load the announcement`, { appearance: 'error' })
        })
        .finally(() => {
          setIsLoading(false)
          setIsEditing(true)
          setIsEditingRecurring(true)
          setEditingAnnouncementType('schedule')
        })
    } else {
      setIsLoading(false)
    }
  }, [announcementIdBeingEdited, addToast])

  useFirstRender(() => {
    postEvent(plausibleEvents.ViewAnnouncementEditor)
  }, [postEvent, plausibleEvents.ViewAnnouncementEditor])

  useEffect(() => {
    function getNextStep() {
      if (
        activeStep === steps.mainTemplate &&
        audience === inPropertyAudienceTypes.DayBeforeCheckOut
      ) {
        return steps.mainPromo
      }

      return null
    }

    setNextStep(getNextStep())
  }, [activeStep, audience])

  const [isShowingConfirmation, setIsShowingConfirmation] = useState(false)

  if (isShowingConfirmation) {
    return (
      <AnnouncementConfirmation
        audience={audience}
        totalEligibleForText={totalEligibleForText}
        committedRemaining={committedRemaining}
        announcementType={isEditing ? editingAnnouncementType : announcementType}
        handleCancel={() => setIsShowingConfirmation(false)}
        handleConfirm={isEditing ? updateAnnouncement : sendAnnouncement}
        closeEditTextDialog={onClose}
        isEditing={isEditing}
        defaultIsRecurring={isEditingRecurring}
        defaultRepeatKind={editingAnnouncementRepeatKind}
        defaultDaysOfTheWeek={editingAnnouncementDaysOfWeek}
        defaultStartDate={editingStartDate}
        defaultEndDate={editingEndDate}
      />
    )
  }

  function sendTestMessage() {
    setIsSendingTestMessage(true)

    announcementApi
      .sendTestMessage({
        audience,
        sendExactlyOn: null,
        messageTemplate,
        mediaUrls: [messageMedia],
        promotionCode,
        minNightsToStay,
        promotionTagline: promoTagline,
        maxDiscountPercentage,
        promoValidityStartDate,
        promoValidityEndDate,
        hasPromoValidityDate
      })
      .then(() => {
        addToast('Test message sent', { appearance: 'success' })
      })
      .catch(error => {
        addToast(getApiErrorMessage(error) || `Couldn't send the test message`, {
          appearance: 'error'
        })
      })
      .finally(() => {
        setIsSendingTestMessage(false)
      })
  }

  function updateAnnouncement({
    date = null,
    isRecurring,
    repeatKind,
    daysOfTheWeek,
    endDate
  }) {
    announcementApi
      .update({
        recurringAnnouncementId: announcementIdBeingEdited,
        audience,
        sendExactlyOn: editingAnnouncementType === 'schedule' ? date : null,
        startDate: isRecurring ? date : null,
        messageTemplate,
        mediaUrls: [messageMedia],
        promotionCode,
        minNightsToStay,
        promotionTagline: promoTagline,
        maxDiscountPercentage,
        promoValidityStartDate,
        promoValidityEndDate,
        hasPromoValidityDate,
        isRepeat: isRecurring,
        repeatKind: isRecurring ? repeatKind : null,
        daysOfWeeks: isRecurring ? daysOfTheWeek : [],
        endDate: isRecurring ? endDate : null
      })
      .then(() => {
        onClose()
        refreshResults()
      })
      .catch(error => {
        addToast(`${error.message.toString()}`, {
          appearance: 'error'
        })
      })
  }

  function sendAnnouncement({
    date = null,
    isRecurring,
    repeatKind,
    daysOfTheWeek,
    endDate
  }) {
    postEvent(plausibleEvents.CreateAnnouncement)

    announcementApi
      .add({
        audience,
        startDate: date,
        endDate: endDate,
        messageTemplate,
        mediaUrls: [messageMedia],
        promotionCode,
        minNightsToStay,
        promotionTagline: promoTagline,
        maxDiscountPercentage,
        promoValidityStartDate,
        promoValidityEndDate,
        hasPromoValidityDate,
        isRepeat: isRecurring,
        repeatKind: isRecurring ? repeatKind : null,
        daysOfWeeks: isRecurring ? daysOfTheWeek : []
      })
      .then(response => {
        if (response?.committedRemaining != null && onScheduledCountUpdated) {
          onScheduledCountUpdated(response.committedRemaining)
        }
        onClose()
        // Debounce list/capacity refresh so DeliverySize/DeliveryRemaining are committed first
        setTimeout(() => {
          refreshResults()
        }, 2500)
      })
      .catch(error => {
        addToast(`${error.message.toString()}`, {
          appearance: 'error'
        })
      })
  }

  function handleScheduleOrSend() {
    if (!isHotelSetupCompleted) {
      addToast(
        'Your initial payment is pending. Please complete it to send announcements.',
        {
          appearance: 'error',
          autoDismiss: true
        }
      )

      return
    }

    setIsShowingConfirmation(true)
  }

  if (isLoading) {
    return <LoadingIndicator white={true} />
  }

  return (
    <Dialog>
      <div className={styles.resolutionWrapper}>
        <div className={styles.dialogWrapper}>
          <div className={styles.mainContent}>
            {Object.values(getSteps(audience)).length > 1 && (
              <div className={styles.navigationSidebar}>
                <div>
                  <div className={styles.dialogDesc}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div>
                        <span className={styles.deliveryName}>Announcement</span>
                        <p className={styles.status}>
                          {inPropertyAudiences[audience].name}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={styles.controls}>
                  <div className={styles.steps}>
                    {Object.values(getSteps(audience)).map(step => (
                      <button
                        key={step}
                        onClick={() => setActiveStep(step)}
                        className={step === activeStep ? styles.activeStep : styles.step}
                      >
                        <span className={styles.paginatorNo}>{stepNames[step]}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div className={styles.editTextHolder}>
              <div className={styles.editTextContent}>
                {Object.values(getSteps(audience)).length === 1 && (
                  <div className={styles.title}>
                    <h2 className={styles.dialogTitle}>Announcement</h2>
                    <div className={styles.dialogDesc}>
                      {inPropertyAudiences[audience].name}
                    </div>
                  </div>
                )}
                {activeStep === steps.mainTemplate && (
                  <MainTemplate
                    messageTemplate={messageTemplate}
                    onTextChange={handleTextChange}
                    setUsedAllTags={setUsedAllTags}
                    usedAllTags={usedAllTags}
                    messageMedia={messageMedia}
                    setMessageMedia={setMessageMedia}
                    isAllTagsRequired={true}
                    mediaTag={mediaTag}
                    requiredTagIds={['__guestName__', '__hotelName__']}
                    isMediaRequired={false}
                    isEditing={isEditing}
                  />
                )}
                {activeStep === steps.mainPromo && (
                  <EditPromo
                    promoCode={promotionCode}
                    setPromoCode={setPromotionCode}
                    minNights={minNightsToStay}
                    setMinNights={setMinNightsToStay}
                    maxDiscount={maxDiscountPercentage}
                    setMaxDiscount={setMaxDiscountPercentage}
                    setPromoTagline={setPromoTagline}
                    promoTagline={promoTagline}
                    hasPromoValidityDateControls={false}
                    hasPromoValidityDate={hasPromoValidityDate}
                    setHasPromoValidityDate={setHasPromoValidityDate}
                    promoValidityStartDate={promoValidityStartDate}
                    setPromoValidityStartDate={setPromoValidityStartDate}
                    promoValidityEndDate={promoValidityEndDate}
                    setPromoValidityEndDate={setPromoValidityEndDate}
                    promoToggleLabel="Add link with promotion"
                    isUsingDiscount={isUsingDiscount}
                    setIsUsingDiscount={setIsUsingDiscount}
                    isPromoValid={isPromoValid}
                  />
                )}
              </div>
              <div className={styles.submitHolder}>
                <SecondaryRegularButton onClick={() => onClose()}>
                  Cancel
                </SecondaryRegularButton>
                {nextStep ? (
                  <PrimaryRegularButton
                    className={styles.nextButton}
                    onClick={() => setActiveStep(nextStep)}
                    disabled={messageTemplate.length === 0 || !usedAllTags}
                  >
                    Next
                  </PrimaryRegularButton>
                ) : (
                  <PrimaryRegularButton
                    disabled={messageTemplate.length === 0 || !usedAllTags}
                    onClick={handleScheduleOrSend}
                  >
                    {isEditing ? (
                      'Next'
                    ) : (
                      <>
                        {announcementType === 'schedule' ? 'Schedule' : 'Send'}{' '}
                        announcement
                      </>
                    )}
                  </PrimaryRegularButton>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className={styles.previewHolder}>
          <div className={styles.previewControlHolder}>
            <button onClick={sendTestMessage} disabled={isSendingTestMessage}>
              <SmsIcon />
              {isSendingTestMessage ? 'Sending...' : 'Send yourself a test message'}
            </button>
          </div>
          <TextPreview>
            <TextPreviewBubble
              text={messageTemplate}
              defaultData={{ userName, hotelName }}
              hasOptOutText
              hasLink={!!promotionCode}
            />
            {messageMedia && (
              <img
                src={getImgUrlFromCDN(messageMedia, mediaTag, hotelId)}
                alt="campaign media"
                className={styles.campaignMedia}
              />
            )}
          </TextPreview>
        </div>
      </div>
    </Dialog>
  )
}

export default EditTextDialog
